import { z } from 'zod';

/**
 * Client-side form schemas only check shape and required fields so users get instant feedback;
 * the API stays the authority (its 400 messages are mapped back onto the fields).
 * Form values are strings (HTML inputs); conversion to numbers/null happens in each screen's toPayload.
 */
export const req = (msg = 'Required') => z.string().trim().min(1, msg);
export const optStr = z.string().trim().optional();
export const selectReq = (msg = 'Select an option') => z.string().min(1, msg);
export const selectOpt = z.string().optional();

const MONEY = /^\d{1,16}(\.\d{1,2})?$/;
const SIGNED_MONEY = /^-?\d{1,16}(\.\d{1,2})?$/;
const RATE = /^\d{1,14}(\.\d{1,4})?$/;
const FX_RATE = /^\d{1,10}(\.\d{1,8})?$/;

export const amountReq = z
  .string()
  .trim()
  .min(1, 'Required')
  .regex(MONEY, 'Use a number with up to 2 decimals')
  .refine((s) => Number(s) > 0, 'Must be greater than zero');
export const rateReq = z
  .string()
  .trim()
  .min(1, 'Required')
  .regex(RATE, 'Use a number with up to 4 decimals')
  .refine((s) => Number(s) > 0, 'Must be greater than zero');
/** Daily exchange rate / invoice line rate per 1 unit of the base currency: up to 8 decimals (a rate like 0.055 needs them). */
export const fxRateReq = z
  .string()
  .trim()
  .min(1, 'Required')
  .regex(FX_RATE, 'Use a number with up to 8 decimals')
  .refine((s) => Number(s) > 0, 'Must be greater than zero');
export const balanceOpt = z
  .string()
  .trim()
  .refine((s) => s === '' || SIGNED_MONEY.test(s), 'Use a number with up to 2 decimals')
  .optional();
export const emailOpt = z
  .string()
  .trim()
  .refine((s) => s === '' || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s), 'Enter a valid email')
  .optional();
const MOBILE = /^\d{10}$/;
export const mobileOpt = z
  .string()
  .trim()
  .refine((s) => s === '' || MOBILE.test(s), 'Enter a 10-digit mobile number (numbers only)')
  .optional();
/** Required variants. The legacy screens made these mandatory in the UI, so the forms keep doing so (the API stays lenient). */
export const emailReq = z.string().trim().min(1, 'Enter an email').regex(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, 'Enter a valid email');
export const mobileReq = z.string().trim().min(1, 'Enter a mobile number').regex(MOBILE, 'Enter a 10-digit mobile number (numbers only)');
/** Digits only, any length (no 10-digit requirement) - e.g. agent mobile numbers, which may be 12-15 digits. */
const DIGITS = /^\d+$/;
export const digitsOpt = z
  .string()
  .trim()
  .refine((s) => s === '' || DIGITS.test(s), 'Numbers only')
  .optional();
export const digitsReq = (msg = 'Enter a mobile number') => z.string().trim().min(1, msg).regex(DIGITS, 'Numbers only');
export const balanceReq = z
  .string()
  .trim()
  .min(1, 'Enter the opening balance (0 if none)')
  .regex(SIGNED_MONEY, 'Use a number with up to 2 decimals');
export const accountNoReq = z
  .string()
  .trim()
  .min(1, 'Enter the account number')
  .regex(/^[A-Za-z0-9\- ]{4,30}$/, 'Account number: letters, numbers, spaces or hyphens, 4-30 characters');
const ALPHA = /^[A-Za-z][A-Za-z' -]*$/;
/** Letters only (spaces/apostrophe/hyphen allowed), must start with a letter. For Name/Country-style fields. */
export const alphaReq = (msg = 'Required') => z.string().trim().min(1, msg).regex(ALPHA, 'Letters only, no digits or symbols');
export const alphaOpt = z
  .string()
  .trim()
  .refine((s) => s === '' || ALPHA.test(s), 'Letters only, no digits or symbols')
  .optional();
export const dateTimeReq = z.string().min(1, 'Required');
export const dateReq = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Select a date');
/** ISO 4217-style currency code: exactly 3 letters. Mirrors the server's `code` rule. */
export const currencyCodeReq = z.string().trim().regex(/^[A-Za-z]{3}$/, 'Use a 3-letter code, e.g. MYR');

/** New-password policy (8+ characters, a letter and a digit) - mirrors the server's `newPassword` rule. */
export const newPasswordReq = z.string().min(8, 'At least 8 characters').regex(/[A-Za-z]/, 'Must contain a letter').regex(/\d/, 'Must contain a digit');
/**
 * Same rule as a plain validation function, for a password field whose required-ness is conditional
 * (e.g. required when creating a user, optional when editing one - '' there means "leave unchanged").
 */
export function newPasswordIssue(p: string | undefined, required: boolean): string | null {
  if (!p) return required ? 'Required' : null;
  if (p.length < 8) return 'At least 8 characters';
  if (!/[A-Za-z]/.test(p)) return 'Must contain a letter';
  if (!/\d/.test(p)) return 'Must contain a digit';
  return null;
}

// ---- payload conversion helpers
export const toId = (v: unknown): number | undefined => (v === '' || v === undefined || v === null ? undefined : Number(v));
export const toIdOrNull = (v: unknown): number | null => (v === '' || v === undefined || v === null ? null : Number(v));
export const toText = (v: unknown): string | null => {
  const s = typeof v === 'string' ? v.trim() : '';
  return s === '' ? null : s;
};
export const toMoney = (v: unknown): string | undefined => {
  const s = typeof v === 'string' ? v.trim() : '';
  return s === '' ? undefined : s;
};
